import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, Mail, FileText } from 'lucide-react';

const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration]);

  return (
    <div className="text-5xl md:text-6xl font-light text-white mb-2">
      {count}
      {suffix}
    </div>
  );
};

const SurveillanceCounter = ({ language }) => {
  const [count, setCount] = useState(47391);
  const ref = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(c => c + Math.floor(Math.random() * 7) + 1);
    }, 800 + Math.random() * 1200);
    return () => clearInterval(interval);
  }, []);

  return (
    <p className="text-5xl md:text-7xl font-light text-red-400 font-mono tabular-nums" ref={ref} data-testid="surveillance-count">
      {count.toLocaleString(language === 'zh' ? 'zh-CN' : language === 'ko' ? 'ko-KR' : language === 'ru' ? 'ru-RU' : 'fr-FR')}
    </p>
  );
};

const Home = () => {
  const { t, language } = useLanguage();
  const [currentHero, setCurrentHero] = useState(0);
  const heroTitles = [t.hero.title1, t.hero.title2, t.hero.title3];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHero((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1920&h=1080&fit=crop')`,
            filter: 'brightness(0.4)'
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />

        {/* Ampersand Symbol */}
        <div className="absolute top-1/4 text-[20rem] font-light text-[#d4c5a9]/10 pointer-events-none">
          &
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-9xl font-thin tracking-[0.1em] md:tracking-[0.2em] text-white mb-6 md:mb-8 transition-opacity duration-1000 break-words">
            {heroTitles[currentHero]}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-light text-gray-300 tracking-wide max-w-3xl mx-auto px-4">
            {t.hero.subtitle}
          </p>
        </div>

        {/* Scroll Indicator */}
        <button 
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-white/70 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </section>

      {/* About Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-8xl font-light text-[#d4c5a9] mb-8">&</div>
          </div>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed text-center mb-12">
            {t.about.description}
          </p>
          <div className="text-center">
            <a
              href="mailto:contact@global-intelligence.fr?subject=Demande%20de%20mission%20%E2%80%93%20URGENT&body=Cher%20GlobalIntel%2C%0A%0AJ%27ai%20besoin%20de%20vos%20services%20pour%20une%20mission%20de%20la%20plus%20haute%20importance%20%3A%0A%0A-%20Cible%20%3A%20Mon%20patron%20(il%20m%27a%20refus%C3%A9%20une%20augmentation%20pour%20la%203%C3%A8me%20fois)%0A-%20Type%20%3A%20Compromission%20%2B%20chantage%20discret%0A-%20Budget%20%3A%20Mon%20PEL%20%2B%203%20bitcoin%20que%20j%27ai%20min%C3%A9s%20en%202014%0A-%20D%C3%A9lai%20%3A%20Avant%20l%27entretien%20annuel%20de%20mars%0A%0AEnvoyez%20Tatiana%20si%20possible.%20Ou%20Olga.%20Ou%20les%20deux.%0A%0ACordialement%2C%0AUn%20salari%C3%A9%20d%C3%A9sesp%C3%A9r%C3%A9"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#d4c5a9] hover:bg-[#e5d7bb] text-black font-medium rounded transition-all duration-300 hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              {t.about.cta}
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="text-center">
            <AnimatedCounter end={20} suffix="+" />
            <p className="text-gray-400 tracking-wide">{t.stats.years}</p>
          </div>
          <div className="text-center">
            <AnimatedCounter end={100} suffix="+" />
            <p className="text-gray-400 tracking-wide">{t.stats.countries}</p>
          </div>
          <div className="text-center">
            <AnimatedCounter end={6} />
            <p className="text-gray-400 tracking-wide">{t.stats.offices}</p>
          </div>
        </div>
      </section>

      {/* Surveillance Counter */}
      <section className="py-12 px-6 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-r from-red-950/40 via-black to-red-950/40 border border-red-500/20 rounded-xl p-8 overflow-hidden" data-testid="surveillance-counter">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
            <div className="text-center">
              <p className="text-red-400/70 text-xs font-mono tracking-[0.3em] uppercase mb-3">
                {language === 'ru' ? 'СИСТЕМА МОНИТОРИНГА' : language === 'ko' ? '\uBAA8\uB2C8\uD130\uB9C1 \uC2DC\uC2A4\uD15C' : language === 'zh' ? '\u76D1\u63A7\u7CFB\u7EDF' : 'MONITORING EN TEMPS R\u00C9EL'}
              </p>
              <SurveillanceCounter language={language} />
              <p className="text-gray-500 text-sm mt-3 font-mono">
                {language === 'ru' ? '\u0430\u0433\u0435\u043D\u0442\u043E\u0432 \u0437\u0430\u043F\u0430\u0434\u043D\u044B\u0445 \u0441\u043B\u0443\u0436\u0431 \u043F\u043E\u0441\u0435\u0442\u0438\u043B\u0438 \u044D\u0442\u043E\u0442 \u0441\u0430\u0439\u0442' : language === 'ko' ? '\uC11C\uBC29 \uCCA9\uBCF4 \uC694\uC6D0\uC774 \uC774 \uC0AC\uC774\uD2B8\uB97C \uBC29\uBB38\uD588\uC2B5\uB2C8\uB2E4' : language === 'zh' ? '\u540D\u897F\u65B9\u60C5\u62A5\u4EBA\u5458\u8BBF\u95EE\u4E86\u672C\u7AD9' : "agents des services occidentaux ont visit\u00E9 ce site"}
              </p>
              <div className="flex justify-center gap-8 mt-6 text-xs font-mono text-gray-600">
                <span>CIA: 47%</span>
                <span>MI6: 23%</span>
                <span>DGSE: 12%</span>
                <span>BND: 8%</span>
                <span>Mossad: 10%</span>
              </div>
              <Link to="/rapport" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-red-900/50 hover:bg-red-800/60 text-red-300 text-sm font-medium rounded-lg transition-all border border-red-500/20 hover:border-red-500/40" data-testid="annual-report-link">
                <FileText className="w-4 h-4" />
                {language === 'ru' ? 'Годовой отчёт 2025' : language === 'ko' ? '2025 연례 보고서' : language === 'zh' ? '2025年度报告' : "Rapport Annuel d'Activité 2025"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-white text-center mb-16 tracking-wide">
            {t.services.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.services.items.map((service, index) => {
              const href = service.title.includes('🔞') ? '/special-services' : '/services';
              return (
                <Link
                  key={index}
                  to={href}
                  className="group p-8 bg-gradient-to-br from-gray-900/50 to-gray-900/30 border border-gray-800 rounded-lg hover:border-[#d4c5a9]/50 transition-all duration-300 hover:scale-105 cursor-pointer"
                  data-testid={`service-tile-${index}`}
                >
                  <h3 className="text-xl font-medium text-[#d4c5a9] mb-4 group-hover:text-[#e5d7bb] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* International Presence */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-white text-center mb-8 tracking-wide">
            {t.regions.title}
          </h2>
          <p className="text-lg text-gray-400 text-center mb-16 max-w-3xl mx-auto">
            {t.regions.subtitle}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.regions.items.map((region, index) => (
              <Link
                key={index}
                to="/zones"
                className="group relative overflow-hidden rounded-lg aspect-square bg-gray-900 border border-gray-800 hover:border-[#d4c5a9]/50 transition-all duration-300 cursor-pointer"
                data-testid={`zone-tile-${index}`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent z-10" />
                <div className="absolute inset-0 p-6 flex flex-col items-center justify-center text-center z-20">
                  <h3 className="text-lg font-medium text-white mb-3 group-hover:text-[#d4c5a9] transition-colors">
                    {region.name.replace(/\s*[\u{1F1E0}-\u{1F1FF}]{2}\s*/gu, '')}
                  </h3>
                  <span className="text-4xl mb-3">{(region.name.match(/[\u{1F1E0}-\u{1F1FF}]{2}/gu) || [''])[0]}</span>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {region.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8 tracking-wide">
            {t.team.title}
          </h2>
          <div className="mb-16">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop"
              alt="Team"
              className="w-full max-w-4xl mx-auto rounded-lg shadow-2xl"
            />
          </div>
          <Link
            to="/management"
            className="inline-block px-8 py-4 bg-[#d4c5a9] hover:bg-[#e5d7bb] text-black font-medium rounded transition-all duration-300 hover:scale-105 tracking-wider"
          >
            {t.team.cta}
          </Link>

          {/* Team Stats */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <AnimatedCounter end={30} suffix="+" />
              <p className="text-gray-400 tracking-wide">{t.team.stats.languages}</p>
            </div>
            <div className="text-center">
              <AnimatedCounter end={2800} suffix="+" />
              <p className="text-gray-400 tracking-wide">{t.team.stats.cases}</p>
            </div>
            <div className="text-center">
              <AnimatedCounter end={5000} suffix="+" />
              <p className="text-gray-400 tracking-wide">{t.team.stats.sources}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;